// ── THE ARC-IC SCREENER, ON SCREEN — one farm, one state, a customized run ────────────────
//
// Kevin, 2026-10-07: *"a screener by farm ... Something where we can pull up a farm and run
// it to see how it looks. A customized run."*
//
// ⚠ IT IS NOT IN THE COVERAGE ADVISOR'S COUNTY PANEL, AND THAT IS STRUCTURAL.
// The Advisor is scoped to ONE crop and ONE county, and its farm picker is deliberately
// county-scoped — its selection resets when the county changes, because the two inputs it
// asks for (the irrigated share, the FSA payment yield) are per farm IN A COUNTY. ARC-IC is
// the opposite shape: whole-farm, state-wide, across every covered commodity at once. Siting
// it inside that panel would mean a picker that silently dropped half the farm whenever the
// page's county happened to change, so this fetches its own rows for its own scope.
//
// ⚠ EVERY NUMBER A HUMAN TYPES IS REQUIRED AND NONE IS DEFAULTED. FSA base acres, the 2026
// yield per crop, and the confirmation that we can see the whole pool. Each one has a
// mechanism behind the refusal rather than a comment:
//   · base acres scale the payment and are nowhere in this database;
//   · the 2026 yield is the farm's FSA-reported production, not our monitor acreage;
//   · the pool is the crops Silas INSURES, which may be narrower than FSA's — and a
//     narrower pool OVERSTATES the payment whenever the crop we cannot see did better.
//
// The arithmetic is ./arcIcRule's; the assembly is ./arcIcScreen's; the ARC-CO side comes in
// as `countyReadFn` so this screen cannot grow a second copy of the engine three other
// surfaces already share.
import React, { useState, useMemo, useCallback } from "react";
import {
  loadArcIcCandidates, buildArcIcPool, arcCoComparison, screenArcIc,
  benchmarkYieldFor, INPUT_SOURCES,
} from "./arcIcScreen";
import { ARC_IC_BREAKEVEN_MULTIPLE } from "./arcIcRule";
// ⚠ ONE COLUMN LIST, NOT A SECOND COPY. `ARC_CO_SELECT` is the select the one-page election
// summary and the PDF tool already read `arc_co_county` with; a list typed again here would
// drift the day FSA's feed gains a column, and a rule reading a column its own query never
// asked for is dead code that still passes every test (`check-query-contract`'s whole point).
import { ARC_CO_SELECT } from "./arcPlcFarmSummary";

const d0 = (v) => (v == null || !isFinite(v) ? "—" : Math.round(v).toLocaleString());
const d2 = (v) => (v == null || !isFinite(v) ? "—" : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const d1 = (v) => (v == null || !isFinite(v) ? "—" : v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const pct1 = (v) => (v == null || !isFinite(v) ? "—" : (v * 100).toFixed(1) + "%");
const numOrNull = (v) => (v === "" || v == null || !isFinite(Number(v)) ? null : Number(v));

const S = {
  card: { background: "#fff", border: "1px solid #d5e2d5", borderRadius: 12, padding: "16px 18px", marginBottom: 18 },
  h: { fontSize: 13, fontWeight: 800, color: "#1a2e1a", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 },
  sub: { fontSize: 11, color: "#60735f", lineHeight: 1.5 },
  row: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  sel: { fontSize: 12, padding: "5px 8px", borderRadius: 5, border: "1px solid #cdd9cd", background: "#fff" },
  inp: { fontSize: 12, padding: "5px 7px", borderRadius: 5, border: "1px solid #cdd9cd", background: "#fff", width: 86, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  btn: (on) => ({ fontSize: 12, fontWeight: 800, padding: "6px 13px", borderRadius: 7, border: "1px solid #1a2e1a", background: on ? "#fff" : "#f0f3f0", color: on ? "#1a2e1a" : "#677167", cursor: on ? "pointer" : "not-allowed" }),
  warn: { fontSize: 11.5, lineHeight: 1.55, background: "#fff8ec", border: "1px solid #e8d9a8", color: "#7a5a00", borderRadius: 7, padding: "8px 10px" },
  bad: { fontSize: 11.5, lineHeight: 1.55, background: "#fdf1ec", border: "1px solid #eccfc0", color: "#a5461b", borderRadius: 7, padding: "8px 10px" },
  need: { fontSize: 10.5, fontWeight: 700, color: "#a5461b", marginLeft: 4 },
  est: { fontSize: 10, fontWeight: 700, color: "#7a5a00", background: "#fff8ec", border: "1px solid #e8d9a8", borderRadius: 4, padding: "1px 5px", marginLeft: 5 },
  th: { padding: "4px 8px", textAlign: "right", fontSize: 10.5, fontWeight: 800, color: "#5c6f5c", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e0ece0" },
  thl: { padding: "4px 8px", textAlign: "left", fontSize: 10.5, fontWeight: 800, color: "#5c6f5c", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e0ece0" },
  td: { padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12 },
  tdl: { padding: "6px 8px", textAlign: "left", fontSize: 12 },
};

/**
 * ArcIcScreenerPanel
 *
 * @param farmers      the whole book — ARC-IC is whole-farm, so the picker is not scoped
 * @param fetchRows    the caller's REST read (sbFetch)
 * @param programYear  the ONE ARC/PLC program-year context; required, never defaulted here
 * @param countyReadFn ./arcPlcElection's `countyRead`, injected so the ARC-CO side is the
 *                     same engine the Advisor and the election summary use
 * @param resolvePracticeRowFn ./arcPlcElection's `resolvePracticeRow`
 * @param showDollars  false on Farm Programs: the result states whether ARC-IC pays and how
 *                     it sits against ARC-CO, with no payment dollars (Kevin, 2026-11-01: "Do
 *                     not include dollars in ARC/PLC data"). Prices still print.
 */
export default function ArcIcScreenerPanel({
  farmers = [], fetchRows, programYear = null,
  countyReadFn = null, resolvePracticeRowFn = null, showDollars = true,
}) {
  const [farmId, setFarmId] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [cand, setCand] = useState(null);          // loadArcIcCandidates result
  const [stateSel, setStateSel] = useState("");
  const [baseAcres, setBaseAcres] = useState("");
  const [poolOk, setPoolOk] = useState(false);
  const [actual, setActual] = useState({});        // crop -> typed 2026 yield
  const [benchOv, setBenchOv] = useState({});      // crop -> typed benchmark yield
  const [arcCoRows, setArcCoRows] = useState(null);

  const farmOpts = useMemo(() => (farmers || [])
    .filter((f) => f && f.id)
    .map((f) => ({ id: f.id, name: f.name || f.farmer_name || ("Farm " + String(f.id).slice(0, 6)) }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name))), [farmers]);

  // ⚠ CHANGING THE FARM CLEARS EVERY TYPED ANSWER. They are per farm — a base acreage or a
  // 2026 yield carried from the previous grower would price this one on another's figures,
  // which is the hazard `wheatPlannedKey` and the Advisor's own per-farm key already record.
  const pickFarm = (id) => {
    setFarmId(id); setCand(null); setStateSel(""); setArcCoRows(null);
    setBaseAcres(""); setPoolOk(false); setActual({}); setBenchOv({}); setErr(null);
  };

  const run = useCallback(async () => {
    if (!farmId || programYear == null) return;
    setLoading(true); setErr(null); setCand(null); setArcCoRows(null);
    try {
      // ⚠ A FARM MAY BE SEVERAL `farmers` ROWS (the CY2027 rollover minted five for one
      // grower), so the id list is built from every row sharing the chosen name.
      const picked = farmOpts.find((f) => f.id === farmId);
      const ids = (farmers || []).filter((f) => f && f.id
        && String(f.name || f.farmer_name || "").trim().toUpperCase() === String(picked && picked.name || "").trim().toUpperCase())
        .map((f) => f.id);
      const c = await loadArcIcCandidates(fetchRows, { farmerIds: ids.length ? ids : [farmId], programYear });
      setCand(c);
      setStateSel(c.states.length === 1 ? c.states[0].state : "");
      // The ARC-CO comparison rows for every state the farm is in, in one read.
      const crops = [...new Set(c.states.flatMap((s) => s.crops.map((x) => x.crop)))];
      const sts = c.states.map((s) => s.state);
      if (crops.length && sts.length) {
        const rows = await fetchRows("arc_co_county", {
          select: ARC_CO_SELECT,
          filters: [`program_year=eq.${programYear}`, `crop=in.(${crops.map((x) => '"' + x + '"').join(",")})`, `state=in.(${sts.join(",")})`],
        });
        setArcCoRows(rows || []);
      } else setArcCoRows([]);
    } catch (e) { setErr(String((e && e.message) || e)); }
    finally { setLoading(false); }
  }, [farmId, programYear, farmers, farmOpts, fetchRows]);

  const stRec = useMemo(() => (cand && cand.states.find((s) => s.state === stateSel)) || null, [cand, stateSel]);

  // ── the pool, from the loaded candidates plus whatever a human has typed ────────────────
  const pool = useMemo(() => {
    if (!stRec) return null;
    return buildArcIcPool({
      state: stRec.state, programYear: cand.programYear,
      crops: stRec.crops.map((c) => ({
        crop: c.crop, state: stRec.state,
        plantedAcres: c.plantedAcres, plantedSource: c.plantedSource,
        benchmark: numOrNull(benchOv[c.crop]) != null
          ? benchmarkYieldFor({ crop: c.crop, typed: numOrNull(benchOv[c.crop]) })
          : c.benchmark,
        actualYield: numOrNull(actual[c.crop]),
        actualSource: numOrNull(actual[c.crop]) != null ? "typed" : "missing",
        benchmarkPrice: c.benchmarkPrice, myaPrice: c.myaPrice,
      })),
    });
  }, [stRec, cand, actual, benchOv]);

  // ── the ARC-CO side, through the INJECTED engine ────────────────────────────────────────
  const arcCo = useMemo(() => {
    if (!stRec || !arcCoRows || !countyReadFn || !resolvePracticeRowFn || !cand) return null;
    const byCell = {};
    for (const r of arcCoRows) {
      if (!r || Number(r.program_year) !== Number(cand.programYear)) continue;
      const k = String(r.crop || "").toUpperCase() + "|" + String(r.state || "").toUpperCase() + "|" + String(r.county || "").toUpperCase().split(".").join("");
      (byCell[k] = byCell[k] || {})[String(r.practice || "ALL").toUpperCase()] = r;
    }
    const reads = [];
    for (const c of stRec.crops) for (const cc of c.counties) {
      const k = c.crop + "|" + stRec.state + "|" + cc.county.split(".").join("");
      // ⚠ NO IRRIGATED SHARE IS SUPPLIED, so a county FSA splits irrigated/non-irrigated
      // resolves to whatever resolvePracticeRow does with an unanswered share — which is a
      // withheld row, not a guessed one. It lands in `unpriced` and is named on screen.
      const res = resolvePracticeRowFn(byCell[k] || null, null);
      const read = countyReadFn({ countyRow: res && res.row, ref: cand.refs[c.crop] || null });
      reads.push({ crop: c.crop, county: cc.county, acres: cc.acres,
        arcPerAcre: read && read.usable ? read.arcPerAcre : null });
    }
    return arcCoComparison({ reads });
  }, [stRec, arcCoRows, countyReadFn, resolvePracticeRowFn, cand]);

  const result = useMemo(() => {
    if (!pool) return null;
    return screenArcIc({ pool, baseAcres: numOrNull(baseAcres), arcCo, poolConfirmed: poolOk ? true : null });
  }, [pool, baseAcres, arcCo, poolOk]);

  const needs = [];
  if (stRec) {
    for (const c of stRec.crops) {
      if (numOrNull(actual[c.crop]) == null) needs.push(c.crop + " " + (cand && cand.programYear ? cand.programYear + " " : "") + "yield");
      const b = numOrNull(benchOv[c.crop]) != null ? numOrNull(benchOv[c.crop]) : (c.benchmark && c.benchmark.value);
      if (b == null) needs.push(c.crop + " benchmark yield");
    }
    if (numOrNull(baseAcres) == null) needs.push("FSA base acres");
  }

  return (
    <div style={S.card}>
      <div style={S.h}>ARC Individual Coverage — farm screener</div>
      <div style={{ ...S.sub, marginBottom: 10 }}>
        {/* The scope sentence is first because it is what decides whether a reader should
            believe the number at the bottom. */}
        ARC-IC is elected per <b>FSA farm</b> and pays on <b>65% of base acres</b> against{" "}
        <b>one whole-farm, state-wide revenue</b> — a good crop offsets a bad one. ARC-CO and PLC
        pay on 85% of the same base acres, so ARC-IC has to pay{" "}
        <b>{ARC_IC_BREAKEVEN_MULTIPLE.toFixed(4).replace(/0+$/, "")}×</b> more per acre to break even.
        This screener answers whether it is worth taking to the FSA office — it is not a payment
        calculation and it elects nothing.
      </div>

      <div style={{ ...S.row, marginBottom: 10, padding: "7px 10px", background: "#f7faf7", border: "1px solid #e0ece0", borderRadius: 7 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d" }}>Which farm?</span>
        <select value={farmId} onChange={(e) => pickFarm(e.target.value)} style={{ ...S.sel, minWidth: 240 }}>
          <option value="">— select a farm —</option>
          {farmOpts.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <span style={{ fontSize: 11, color: "#60735f" }}>
          {programYear == null ? "no ARC/PLC program year is available" : "program year " + programYear}
        </span>
        <button onClick={run} disabled={!farmId || programYear == null || loading}
          style={{ ...S.btn(!!farmId && programYear != null && !loading), marginLeft: "auto" }}>
          {loading ? "Loading…" : "Run screener"}
        </button>
      </div>

      {err && <div style={S.bad}>Couldn’t load this farm: {err}</div>}

      {cand && !cand.states.length && (
        <div style={S.warn}>
          This farm has no {cand.programYear} coverage Silas can pool. ARC-IC needs at least one
          covered commodity with planted acres and a state on its policy.
          {cand.noState.length ? " " + cand.noState.length + " coverage line(s) record no state, so FSA's county row cannot be matched." : ""}
        </div>
      )}

      {cand && cand.states.length > 1 && (
        <div style={{ ...S.warn, marginBottom: 10 }}>
          {/* ⚠ TWO STATES ARE TWO ELECTIONS AND TWO POOLS. Pooling across them would produce a
              revenue FSA never computes, and it would look exactly like a correct one. */}
          <b>This farm is in {cand.states.length} states ({cand.states.map((s) => s.state).join(", ")}).</b>{" "}
          ARC-IC is elected and pooled <b>per state</b>, so each one is its own run — nothing here
          pools across a state line.
          <div style={{ ...S.row, marginTop: 6 }}>
            {cand.states.map((s) => (
              <button key={s.state} onClick={() => setStateSel(s.state)}
                style={{ ...S.btn(true), background: stateSel === s.state ? "#1a2e1a" : "#fff", color: stateSel === s.state ? "#fff" : "#1a2e1a" }}>
                {s.state}
              </button>
            ))}
          </div>
        </div>
      )}

      {stRec && (
        <>
          {/* ── THE POOL, AND THE CONFIRMATION THAT IT IS THE WHOLE POOL ───────────── */}
          <div style={{ ...S.warn, marginBottom: 10 }}>
            <b>The pool is the crops Silas insures in {stRec.state}:</b>{" "}
            {stRec.crops.map((c) => c.crop + " " + d1(c.plantedAcres) + " ac").join(" · ")}.
            ARC-IC pools <b>every</b> covered commodity planted on <b>all</b> of this producer’s
            ARC-IC farms in the state. If anything else is grown there, the payment below is
            <b> overstated</b> — the good crop that would have offset the bad one is simply absent.
            {cand.notCovered.length > 0 && (
              <div style={{ marginTop: 5 }}>
                Not in the pool: {cand.notCovered.map((n) => n.crop + " (" + d1(n.acres) + " ac, " + n.reason + ")").join("; ")}.
              </div>
            )}
            <label style={{ display: "flex", gap: 7, alignItems: "flex-start", marginTop: 7, cursor: "pointer", fontWeight: 700 }}>
              <input type="checkbox" checked={poolOk} onChange={(e) => setPoolOk(e.target.checked)} style={{ marginTop: 2 }} />
              <span>I have confirmed with the grower that this is every covered commodity they plant in {stRec.state}.</span>
            </label>
          </div>

          {/* ── PER CROP ───────────────────────────────────────────────────────────── */}
          <div style={{ overflowX: "auto", marginBottom: 10 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 620 }}>
              <thead><tr>
                <th style={S.thl}>Crop</th>
                <th style={S.th} title="Insured acres less prevent plant. No bushel comes off a prevent-planted acre, and ARC-IC pools PLANTED acres.">Planted ac</th>
                <th style={S.th} title="The farm's own 5-year Olympic average. A typed figure is the grower's FSA yield history and always wins.">Benchmark bu/ac</th>
                <th style={S.th} title={"The farm's " + (cand && cand.programYear ? cand.programYear + " " : "") + "production per acre, as reported to FSA. Nothing supplies this — it is typed."}>{(cand && cand.programYear) || ""} bu/ac</th>
                <th style={S.th} title="FSA's own ARC benchmark price for the program year.">Bench $</th>
                <th style={S.th} title="FSA's marketing-year average price, a projection until it is final.">MYA $</th>
              </tr></thead>
              <tbody>
                {stRec.crops.map((c) => {
                  const bSrc = numOrNull(benchOv[c.crop]) != null ? "typed" : (c.benchmark && c.benchmark.source) || "missing";
                  const src = INPUT_SOURCES[bSrc] || INPUT_SOURCES.missing;
                  return (
                    <tr key={c.crop} style={{ borderBottom: "1px solid #f0f5f0" }}>
                      <td style={{ ...S.tdl, fontWeight: 700 }}>{c.crop}</td>
                      <td style={S.td} title={c.counties.map((x) => x.county + " " + d1(x.acres)).join(" · ")}>{d1(c.plantedAcres)}</td>
                      <td style={S.td}>
                        <input value={benchOv[c.crop] ?? ""} onChange={(e) => setBenchOv((p) => ({ ...p, [c.crop]: e.target.value }))}
                          onFocus={(e) => e.target.select()} inputMode="decimal"
                          placeholder={c.benchmark && c.benchmark.value != null ? d1(c.benchmark.value) : ""}
                          style={S.inp} />
                        {/* ⚠ AN APH-DERIVED BENCHMARK IS AN ESTIMATE AND IS BADGED AS ONE.
                            RMA's approved yield is built on T-yields, substitution, cups and
                            APH exclusions; FSA's ARC-IC benchmark is the farm's actual yields
                            under FSA's rules. Close, routinely, and not the same number. */}
                        {src.estimate && (
                          <span style={S.est} title={"Olympic average of " + c.benchmark.years + " actual APH years"
                            + (c.benchmark.yearsUsed && c.benchmark.yearsUsed.length ? " (" + c.benchmark.yearsUsed[0] + "–" + c.benchmark.yearsUsed[c.benchmark.yearsUsed.length - 1] + ")" : "")
                            + (c.benchmark.unit ? " in " + c.benchmark.unit : "")
                            + ". APH is not an FSA yield history — type the grower's own figure if you have it."
                            + (c.benchmark.excluded ? " " + c.benchmark.excluded.years + " year(s) in " + c.benchmark.excluded.unit + " were set aside: " + c.benchmark.excluded.why + "." : "")}>est</span>
                        )}
                        {bSrc === "missing" && <span style={S.need} title={(c.benchmark && c.benchmark.why) || "no history"}>needed</span>}
                      </td>
                      <td style={S.td}>
                        <input value={actual[c.crop] ?? ""} onChange={(e) => setActual((p) => ({ ...p, [c.crop]: e.target.value }))}
                          onFocus={(e) => e.target.select()} inputMode="decimal" style={S.inp} />
                        {numOrNull(actual[c.crop]) == null && <span style={S.need}>needed</span>}
                      </td>
                      <td style={S.td}>{c.benchmarkPrice == null ? "—" : "$" + d2(c.benchmarkPrice)}</td>
                      <td style={S.td}>{c.myaPrice == null ? "—" : "$" + d2(c.myaPrice)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ ...S.row, marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d" }}>FSA base acres on this farm</span>
            <input value={baseAcres} onChange={(e) => setBaseAcres(e.target.value)} onFocus={(e) => e.target.select()}
              inputMode="decimal" style={{ ...S.inp, width: 104 }} />
            {/* ⚠ NOT INSURED ACRES AND NOT PLANTED ACRES. FSA's own figure for the farm, from
                the grower's FSA-156EZ — nothing in this database holds it, and substituting
                planted acres would invent the one number the payment scales with. */}
            <span style={{ fontSize: 11, color: "#60735f", maxWidth: 480 }}>
              FSA’s figure from the farm’s FSA-156EZ — <b>not</b> insured or planted acres. Nothing in
              Silas holds base acres, so the payment cannot be sized without it.
            </span>
          </div>

          {needs.length > 0 && (
            <div style={S.warn}>
              <b>Still needed:</b> {needs.join(" · ")}.
            </div>
          )}

          {/* ── THE RESULT ─────────────────────────────────────────────────────────── */}
          {result && result.ok && (
            <div style={{ border: "1px solid #d5e2d5", borderRadius: 9, overflow: "hidden", marginTop: 10 }}>
              <div style={{ background: "#f7faf7", padding: "7px 11px", fontSize: 11.5, fontWeight: 800, color: "#2d4a2d",
                            borderBottom: "1px solid #e0ece0", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span>{stRec.state} · {cand.programYear} · whole-farm pool of {d1(result.pool.revenue.plantedAcres)} planted ac</span>
                <span style={{ marginLeft: "auto", color: result.payment.triggered ? "#1a2e1a" : "#60735f" }}>
                  {result.payment.triggered ? "ARC-IC would pay on these figures" : "ARC-IC would NOT pay on these figures"}
                </span>
              </div>
              {!showDollars && <NoDollarResult result={result} baseAcres={numOrNull(baseAcres)} />}
              {showDollars && <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 480 }}>
                  <tbody>
                    {[
                      ["Benchmark revenue", "$" + d2(result.pool.revenue.benchmarkPerAcre) + "/ac", "acre-weighted across the pool: benchmark yield × FSA benchmark price"],
                      ["90% guarantee", "$" + d2(result.payment.guaranteePerAcre) + "/ac", "ARC-IC's guarantee is 90% of benchmark revenue — ARC-CO's is 86%"],
                      ["Actual revenue", "$" + d2(result.pool.revenue.actualPerAcre) + "/ac", "acre-weighted: " + (cand && cand.programYear ? cand.programYear + " " : "") + "yield × MYA price"],
                      ["Shortfall", "$" + d2(result.payment.shortfallPerAcre) + "/ac",
                        result.payment.cappedOut ? "exceeds the 12% cap, so the cap is what pays" : "below the 12% cap"],
                      ["12% cap", "$" + d2(result.payment.capPerAcre) + "/ac", "12% of benchmark revenue, not of the shortfall"],
                      ["Payment rate", "$" + d2(result.payment.ratePerAcre) + "/ac", result.payment.cappedOut ? "the cap" : "the shortfall"],
                      ["Paid acres", d1(result.payment.paidAcres) + " of " + d1(numOrNull(baseAcres)), "ARC-IC pays on 65% of base acres; ARC-CO and PLC pay on 85%"],
                      ["Gross payment", "$" + d0(result.payment.grossPayment), "payment rate × 65% × base acres"],
                    ].map(([k, v, why]) => (
                      <tr key={k} style={{ borderBottom: "1px solid #f0f5f0" }}>
                        <td style={{ ...S.tdl, color: "#5c6f5c" }}>{k}</td>
                        <td style={{ ...S.td, fontWeight: 700, whiteSpace: "nowrap" }}>{v}</td>
                        <td style={{ ...S.tdl, fontSize: 11, color: "#60735f" }}>{why}</td>
                      </tr>
                    ))}
                    <tr style={{ background: "#f7faf7" }}>
                      <td style={{ ...S.tdl, fontWeight: 800, color: "#1a2e1a" }}>Net payment</td>
                      <td style={{ ...S.td, fontWeight: 800, fontSize: 14, whiteSpace: "nowrap" }}>${d0(result.payment.netPayment)}</td>
                      <td style={{ ...S.tdl, fontSize: 11, color: "#60735f" }}>after 5.7% sequestration</td>
                    </tr>
                  </tbody>
                </table>
              </div>}

              {/* ── vs ARC-CO, computed rather than asserted ─────────────────────── */}
              {showDollars && <div style={{ padding: "9px 11px", borderTop: "1px solid #e0ece0", fontSize: 12 }}>
                {result.comparison.ok ? (
                  <>
                    <div style={{ fontWeight: 800, color: "#2d4a2d", marginBottom: 4 }}>
                      Against ARC-CO, per base acre: ARC-IC ${d2(result.comparison.icPerBaseAcre)} ·
                      ARC-CO ${d2(result.comparison.coPerBaseAcre)}
                    </div>
                    <div style={S.sub}>
                      {/* No winner is stamped — the two figures and the matching rate are the
                          answer, and the election is the grower's with their FSA office. */}
                      ARC-IC would need <b>${d2(result.comparison.icRateToMatchCo)}/ac</b> to match ARC-CO’s
                      ${d2(result.arcCo.ratePerAcre)}/ac rate on this farm, because it pays on 65% of the
                      base acres rather than 85%. ARC-CO is rolled up over {d1(result.arcCo.acres)} ac
                      weighted by <b>{result.arcCo.weightBasis}</b>.
                      {result.arcCo.unpriced.length > 0 && (
                        <> {result.arcCo.unpriced.length} crop/county cell(s) carry no ARC-CO figure
                        ({result.arcCo.unpriced.map((u) => u.crop + "/" + u.county + " — " + u.why).join("; ")}),
                        so the comparison covers part of the farm.</>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={S.sub}>
                    No ARC-CO comparison: {result.comparison.why}. ARC-IC’s 65% payment factor means it
                    has to beat ARC-CO by {ARC_IC_BREAKEVEN_MULTIPLE.toFixed(2)}× per acre, so the
                    comparison is the point of the screener — not the ARC-IC figure alone.
                  </div>
                )}
              </div>}

              {result.thresholds && (
                <div style={{ padding: "9px 11px", borderTop: "1px solid #e0ece0", ...S.sub }}>
                  Single-crop pool, so the yield line is well defined: ARC-IC starts paying below{" "}
                  <b>{pct1(result.thresholds.startsBelow)}</b> of this farm’s own benchmark yield and
                  maxes out at <b>{pct1(result.thresholds.maxesAt)}</b>. Both move with the final MYA —
                  a <i>lower</i> MYA raises them, so falling prices make ARC-IC more likely to pay.
                </div>
              )}

              {result.caveats.length > 0 && (
                <div style={{ padding: "9px 11px", borderTop: "1px solid #e0ece0",
                              background: result.provisional ? "#fff8ec" : "#f7faf7" }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: result.provisional ? "#7a5a00" : "#5c6f5c", marginBottom: 4 }}>
                    {result.provisional ? "⚠ Provisional — check these before acting on it" : "Worth knowing"}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, lineHeight: 1.55, color: result.provisional ? "#7a5a00" : "#5c6f5c" }}>
                    {result.caveats.map((c) => <li key={c.key}>{c.text}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {result && !result.ok && (
            <div style={S.warn}>Not screenable yet: {result.why}.</div>
          )}
        </>
      )}
    </div>
  );
}

// ── THE RESULT WITH NO PAYMENT DOLLARS (Farm Programs) ─────────────────────────────────────
// The same screenArcIc result, said as shares and directions instead of dollars: where the
// pool's revenue sits against its benchmark, whether the 12% cap binds, the paid acres, and
// which side's modeled payment per base acre is larger. No dollar amount prints.
export function NoDollarResult({ result, baseAcres }) {
  const rev = result.pool.revenue, pay = result.payment, cmp = result.comparison;
  const ratio = rev.benchmarkPerAcre > 0 && rev.actualPerAcre != null ? rev.actualPerAcre / rev.benchmarkPerAcre : null;
  const vs = !cmp.ok ? null
    : cmp.icPerBaseAcre <= 0 && cmp.coPerBaseAcre <= 0 ? "Neither ARC-IC nor ARC-CO pays on these figures."
    : cmp.better === "ARC-IC" ? "ARC-IC's modeled payment per base acre is larger than ARC-CO's on these figures."
    : cmp.better === "ARC-CO" ? "ARC-CO's modeled payment per base acre is larger than ARC-IC's on these figures."
    : "ARC-IC and ARC-CO model the same payment per base acre on these figures.";
  const rows = [
    ["Pool revenue", ratio == null ? "—" : pct1(ratio) + " of benchmark", "ARC-IC pays when it falls below 90%"],
    ["12% cap", pay.cappedOut ? "reached" : "not reached", "the shortfall is limited to 12% of benchmark revenue"],
    ["Paid acres", d1(pay.paidAcres) + " of " + d1(baseAcres), "ARC-IC pays on 65% of base acres; ARC-CO and PLC on 85%"],
  ];
  return (
    <>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <tbody>
          {rows.map(([k, v, why]) => (
            <tr key={k} style={{ borderBottom: "1px solid #f0f5f0" }}>
              <td style={{ ...S.tdl, color: "#5c6f5c" }}>{k}</td>
              <td style={{ ...S.td, fontWeight: 700, whiteSpace: "nowrap" }}>{v}</td>
              <td style={{ ...S.tdl, fontSize: 11, color: "#60735f" }}>{why}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ padding: "9px 11px", borderTop: "1px solid #e0ece0", fontSize: 12 }}>
        <div style={{ fontWeight: 800, color: "#2d4a2d" }}>{vs || "No ARC-CO comparison: " + cmp.why + "."}</div>
        {cmp.ok && result.arcCo && result.arcCo.unpriced.length > 0 && (
          <div style={S.sub}>{result.arcCo.unpriced.length} crop/county cell(s) carry no ARC-CO figure, so the comparison covers part of the farm.</div>
        )}
      </div>
    </>
  );
}
